import { Form, Spinner } from 'react-bootstrap';
import classNames from 'classnames';

import PagesHeader from '../../../../../components/base/PagesHeader';
import useSpecialConditions from './useSpecialConditions';

import styles from '../../../../../assets/scss/6-pages/superAdmin/pension-policy/SpecialCondition.module.scss';
import { AbsentTransactionCauseCode, AbsentTransactionCauseCodeWithMeta } from '../../types';
import { getAbsentTransactionCodes } from '../../../../../api/service/superAdmin/pension-policies/service';
import { useEffect, useRef, useState } from 'react';
import useLocale from '../../../../../hooks/useLocale';
import { LocaleCode } from '../../../../../types/locale';
import useContext from '../../PensionPolicyContext/useContext';
import { ABSENT_TRANSACTION_INPUT_MAPPING } from '../../constants';

const OtherAbsenceLeave = () => {
  const { localize, lang } = useLocale();
  const { otherLeave, pushToOtherLeave, removeOtherLeave, toggleCheckedOtherLeaveIncludedIn } = useSpecialConditions();
  const { selectedPolicyDetails } = useContext();
  const [transactionCodes, setTransactionCodes] = useState<AbsentTransactionCauseCodeWithMeta[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasPatchedPolicyDetails = useRef(false);
  const pendingSyncRef = useRef<{ id: string; checked: boolean } | null>(null);
  const pendingIncludedInSyncRef = useRef<{
    id: string;
    key: keyof AbsentTransactionCauseCodeWithMeta['_internal']['includedIn'];
    checked: boolean;
  } | null>(null);

  const isTransactionChecked = (transactionId: string): boolean => {
    return otherLeave.some((entry) => entry.causeCode.id === transactionId);
  };

  const getIncludedInValue = (
    transactionId: string,
    key: keyof AbsentTransactionCauseCodeWithMeta['_internal']['includedIn'],
  ): boolean => {
    const entry = otherLeave.find((item) => item.causeCode.id === transactionId);
    if (!entry) return false;

    const propName = ABSENT_TRANSACTION_INPUT_MAPPING[key];

    return entry[propName] || false;
  };

  const handleCheckedAbsentCode = (id: string, checked: boolean) => {
    pendingSyncRef.current = { id, checked };

    setTransactionCodes((prev) => {
      return prev.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            _internal: {
              ...item._internal,
              checked,
            },
          };
        }
        return item;
      });
    });
  };

  useEffect(() => {
    if (pendingSyncRef.current) {
      const { id, checked } = pendingSyncRef.current;
      const transaction = transactionCodes.find((item) => item.id === id);

      if (transaction) {
        if (checked && !otherLeave.some((entry) => entry.causeCode.id === id)) {
          pushToOtherLeave(transaction);
        } else if (!checked) {
          removeOtherLeave(id);
        }
      }

      pendingSyncRef.current = null;
    }
  }, [transactionCodes, otherLeave, pushToOtherLeave, removeOtherLeave]);

  const handleCheckedAbsentCodeIncludedIn = (
    id: string,
    includedInKey: keyof AbsentTransactionCauseCodeWithMeta['_internal']['includedIn'],
    checked: boolean,
  ) => {
    pendingIncludedInSyncRef.current = { id, key: includedInKey, checked };

    setTransactionCodes((prev) => {
      return prev.map((item) => {
        if (item.id !== id) return item;

        const includedInOption = { ...item._internal.includedIn, [includedInKey]: checked };

        return {
          ...item,
          _internal: { ...item._internal, includedIn: includedInOption },
        };
      });
    });
  };

  useEffect(() => {
    if (pendingIncludedInSyncRef.current) {
      const { id, key, checked } = pendingIncludedInSyncRef.current;

      if (otherLeave.some((entry) => entry.causeCode.id === id)) {
        toggleCheckedOtherLeaveIncludedIn(id, key, checked);
      }

      pendingIncludedInSyncRef.current = null;
    }
  }, [transactionCodes, otherLeave, toggleCheckedOtherLeaveIncludedIn]);

  const fetchAbsentTransactionCodeList = async () => {
    try {
      setIsLoading(true);
      const transactionCodes = (await getAbsentTransactionCodes()) as AbsentTransactionCauseCode[];

      const transactionCodesData = transactionCodes.map((item) => {
        return updateAbsentTransactionCodeData(item, {
          checked: false,
          includedIn: {
            premium: false,
            salary: false,
            insurance: false,
          },
        });
      });

      setTransactionCodes(transactionCodesData);
    } catch (error) {
      console.error('Error fetching absent transaction codes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateAbsentTransactionCodeData = (
    item: AbsentTransactionCauseCode,
    optionChecked: Pick<AbsentTransactionCauseCodeWithMeta['_internal'], 'checked' | 'includedIn'>,
  ): AbsentTransactionCauseCodeWithMeta => {
    return {
      ...item,
      _internal: {
        title: lang === LocaleCode.EN ? item.english : item.swedish || item.english || '',
        checked: optionChecked.checked,
        includedIn: optionChecked.includedIn,
      },
    };
  };

  useEffect(() => {
    fetchAbsentTransactionCodeList();
  }, []);

  useEffect(() => {
    setTransactionCodes((prev) => {
      return prev.map((item) =>
        updateAbsentTransactionCodeData(item, {
          checked: item._internal.checked,
          includedIn: item._internal.includedIn,
        }),
      );
    });
  }, [lang]);

  useEffect(() => {
    if (selectedPolicyDetails && transactionCodes.length > 0 && !hasPatchedPolicyDetails.current) {
      hasPatchedPolicyDetails.current = true;

      if (selectedPolicyDetails.policySettings?.specialConditions?.otherLeave) {
        const otherLeaveItems = selectedPolicyDetails.policySettings.specialConditions.otherLeave;

        setTransactionCodes((prev) => {
          return prev.map((transaction) => {
            const matchingPolicyItem = otherLeaveItems.find((item) => item.causeCode?.id === transaction.id);

            if (matchingPolicyItem) {
              return {
                ...transaction,
                _internal: {
                  ...transaction._internal,
                  checked: matchingPolicyItem.isChecked,
                  includedIn: {
                    premium: matchingPolicyItem.isPremiumCalculation,
                    salary: matchingPolicyItem.isSalaryChange,
                    insurance: matchingPolicyItem.isInsurances,
                  },
                },
              };
            }

            return transaction;
          });
        });
      }
    }
  }, [selectedPolicyDetails, transactionCodes.length]);

  return (
    <div className={classNames('mb-3 parental-check-group', styles.sp_cond_group)}>
      <PagesHeader
        pageTitle={localize('PENSION_POLICY.other_leave')}
        translate={false}
        subText={localize('PENSION_POLICY.new_job_leave')}
        subTextPosition="bottom"
      />
      {/*  <div className="d-flex align-items-end dark-content-section">
        <Container fluid>
          <Row className="w-100">
            <Col>
              <Form.Group className="pe-0">
                <Form.Check
                  inline
                  type="checkbox"
                  label={`${localize('PENSION_POLICY.policy_valid_parental_leave')}`}
                  id="otherLeaveisChecked"
                  className="fs-name mb-1"
                  onChange={(e) => handleToggleOtherLeavePolicy('isChecked', e.target.checked)}
                  name="otherLeaveisChecked"
                  defaultChecked={otherLeave.isChecked}
                  checked={otherLeave.isChecked}
                />
              </Form.Group>
            </Col>

            <Col sm="7">
              {otherLeave.isChecked && (
                <>
                  <p className="fs-name">{localize('PENSION_POLICY.policy_parts_included')}</p>

                  <div className={styles.sp_cond_parts}>
                    <div className={styles.sp_cond_parts__type}>
                      <Form.Group className="pe-0">
                        <Form.Check
                          inline
                          type="checkbox"
                          label={localize('PENSION_POLICY.premium_calculation')}
                          id="otherLeaveisPremiumCalculation"
                          className={classNames('fs-name mb-1', styles.include_part)}
                          onChange={(e) => handleToggleOtherLeavePolicy('isPremiumCalculation', e.target.checked)}
                          name="otherLeaveisPremiumCalculation"
                          defaultChecked={otherLeave.isPremiumCalculation}
                          checked={otherLeave.isPremiumCalculation}
                        />
                      </Form.Group>
                    </div>

                    <div className={styles.sp_cond_parts__type}>
                      <Form.Group className="pe-0">
                        <Form.Check
                          inline
                          type="checkbox"
                          label={localize('PENSION_POLICY.applicable_salary_change')}
                          id="otherLeaveisSalaryChange"
                          className={classNames('fs-name mb-1', styles.include_part)}
                          onChange={(e) => handleToggleOtherLeavePolicy('isSalaryChange', e.target.checked)}
                          name="otherLeaveisSalaryChange"
                          defaultChecked={otherLeave.isSalaryChange}
                          checked={otherLeave.isSalaryChange}
                        />
                      </Form.Group>
                    </div>

                    <div className={styles.sp_cond_parts__type}>
                      <Form.Group className="pe-0">
                        <Form.Check
                          inline
                          type="checkbox"
                          label={localize('insurances')}
                          id="otherLeaveisInsurances"
                          className={classNames('fs-name mb-1', styles.include_part)}
                          onChange={(e) => handleToggleOtherLeavePolicy('isInsurances', e.target.checked)}
                          name="otherLeaveisInsurances"
                          defaultChecked={otherLeave.isInsurances}
                          checked={otherLeave.isInsurances}
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className={styles.sp_other_opt}>
                    <Form.Group className={classNames(styles.child_leave_opt)}>
                      <Form.Label className="fs-name">
                        {localize('PENSION_POLICY.max_length')}{' '}
                        <span className="font-weight-400">({localize('PENSION_POLICY.days')})</span>
                      </Form.Label>
                      <Form.Select
                        value={otherLeave.maximumLength === null ? '' : otherLeave.maximumLength}
                        onChange={(e) =>
                          handleChangeDaysLength(PolicyLeaveCategory.OTHER, 'maximumLength', e.target.value)
                        }
                        name="otherLeaveMaximumOpt"
                        style={{ width: 120 }}
                      >
                        <option value="" disabled>
                          {localize('none')}
                        </option>
                        {CHILD_LEAVE_OPTIONS.map((item) => {
                          return (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </div> */}
      <div className="d-flex align-items-end dark-content-section">
        <div className="table-responsive react-bootstrap-table">
          <table className="table salary-types-table">
            <thead className="header-wrapper">
              <tr className="correcto-table-header">
                <th style={{ maxWidth: 400, width: 400 }}>{localize('PENSION_POLICY.absence_type')}</th>
                <th style={{ maxWidth: 130, width: 130, minWidth: 130 }}>
                  <div className={styles.sp_cond_header_cell}>
                    <span>{localize('GLOBAL.included_in')}</span>
                    <span>{localize('PENSION_POLICY.premium_calculation')}</span>
                  </div>
                </th>
                <th style={{ maxWidth: 130, width: 130, minWidth: 130 }}>
                  <div className={styles.sp_cond_header_cell}>
                    <span>{localize('GLOBAL.included_in')}</span>
                    <span>{localize('PENSION_POLICY.salary_change')}</span>
                  </div>
                </th>
                <th style={{ maxWidth: 130, width: 130, minWidth: 130 }}>
                  <div className={styles.sp_cond_header_cell}>
                    <span>{localize('GLOBAL.included_in')}</span>
                    <span>{localize('PENSION_POLICY.insurances')}</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && transactionCodes.length === 0 && (
                <tr>
                  <td colSpan={4}>{localize('GLOBAL.no_data_available')}</td>
                </tr>
              )}
              {isLoading && (
                <tr>
                  <td colSpan={4}>
                    <Spinner animation="border" role="status" style={{ width: 24, height: 24 }} />
                  </td>
                </tr>
              )}
              {transactionCodes.map((item) => {
                const isChecked = isTransactionChecked(item.id);
                const premiumChecked = getIncludedInValue(item.id, 'premium');
                const salaryChecked = getIncludedInValue(item.id, 'salary');
                const insuranceChecked = getIncludedInValue(item.id, 'insurance');

                return (
                  <tr key={item.id}>
                    <td style={{ maxWidth: 400, width: 400, minWidth: 400 }}>
                      <Form.Group className="pe-0">
                        <Form.Check
                          inline
                          type="checkbox"
                          label={item._internal.title}
                          id={`causename-${item.id}`}
                          name={`causename-${item.id}`}
                          className={classNames('fs-name mb-1')}
                          onChange={(e) => handleCheckedAbsentCode(item.id, e.target.checked)}
                          checked={isChecked}
                        />
                      </Form.Group>
                    </td>
                    <td style={{ maxWidth: 130, width: 130, minWidth: 130 }}>
                      <Form.Group className="pe-0">
                        <Form.Check
                          onChange={(e) => handleCheckedAbsentCodeIncludedIn(item.id, 'premium', e.target.checked)}
                          inline
                          type="checkbox"
                          label=""
                          id={`premium-${item.id}`}
                          name={`premium-${item.id}`}
                          className="fs-name mb-1"
                          disabled={!isChecked}
                          checked={premiumChecked}
                        />
                      </Form.Group>
                    </td>
                    <td style={{ maxWidth: 130, width: 130, minWidth: 130 }}>
                      <Form.Group className="pe-0">
                        <Form.Check
                          onChange={(e) => handleCheckedAbsentCodeIncludedIn(item.id, 'salary', e.target.checked)}
                          inline
                          type="checkbox"
                          label=""
                          id={`salary-${item.id}`}
                          name={`salary-${item.id}`}
                          className="fs-name mb-1"
                          disabled={!isChecked}
                          checked={salaryChecked}
                        />
                      </Form.Group>
                    </td>
                    <td style={{ maxWidth: 130, width: 130, minWidth: 130 }}>
                      <Form.Group className="pe-0">
                        <Form.Check
                          onChange={(e) => handleCheckedAbsentCodeIncludedIn(item.id, 'insurance', e.target.checked)}
                          inline
                          type="checkbox"
                          label=""
                          id={`insurance-${item.id}`}
                          name={`insurance-${item.id}`}
                          className="fs-name mb-1"
                          disabled={!isChecked}
                          checked={insuranceChecked}
                        />
                      </Form.Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OtherAbsenceLeave;
